import { useEffect, useRef, useState } from "react";
import api, { errMsg, exportReport } from "../lib/api";
import Modal, { Field } from "../components/Modal";
import { Plus, ArrowDownLeft, ArrowUpRight, RotateCcw, Pencil, Trash2, Search, PackageX, PackagePlus, FileDown, Truck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const today = () => new Date().toISOString().slice(0, 10);
const CHANNELS = ["Offline", "Shopee", "Tokopedia", "TikTok Shop", "Lazada", "Lainnya"];
const meta = {
  masuk: { label: "Barang Masuk", color: "var(--masuk)", icon: ArrowDownLeft },
  keluar: { label: "Barang Keluar", color: "var(--keluar)", icon: ArrowUpRight },
  retur: { label: "Retur", color: "var(--biru)", icon: RotateCcw },
  hilang: { label: "Hilang", color: "var(--retur)", icon: PackageX },
};

const itemLabel = (i) => `${i.name}${i.warna ? ` (${i.warna})` : ""}`;

// Searchable item picker (typeahead) — works inside scrolling modal
const ItemCombo = ({ value, items, onSelect, testid }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const selected = items.find((i) => i.id === value);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const filtered = items.filter((i) => itemLabel(i).toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#52525B] pointer-events-none" />
        <input
          data-testid={testid}
          className="nb-input w-full pl-8"
          placeholder="Cari / pilih barang…"
          value={open ? q : (selected ? itemLabel(selected) : "")}
          onFocus={() => { setOpen(true); setQ(""); }}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        />
      </div>
      {open && (
        <div data-testid={`${testid}-list`} className="absolute z-30 mt-1 w-full max-h-56 overflow-y-auto bg-white border-2 border-[#0A0A0A] rounded-lg shadow-[3px_3px_0_#0A0A0A]">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-[#52525B]">Tidak ada barang</div>
          ) : filtered.map((i) => (
            <button type="button" key={i.id}
              onClick={() => { onSelect(i.id); setOpen(false); setQ(""); }}
              className={`w-full text-left px-3 py-2 text-sm border-b border-[#0A0A0A]/10 hover:bg-[var(--primary)] ${i.id === value ? "font-bold bg-[var(--primary)]" : ""}`}>
              {itemLabel(i)} <span className="text-[#52525B]">— stok {i.stock} {i.unit}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const emptyReceipt = () => ({ date: today(), brand: "", lines: [{ item_id: "", qty: 1, note: "" }] });

export default function Transactions() {
  const { user } = useAuth();
  const canEdit = !!(user?.is_owner || user?.perms?.mutasi_edit);
  const [txns, setTxns] = useState([]);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ item_id: "", type: "keluar", qty: 1, date: today(), note: "", tipe: "Offline" });
  const [filter, setFilter] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [qItem, setQItem] = useState("");
  const [qTipe, setQTipe] = useState("");
  // goods-receipt (surat terima barang) modal
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receipt, setReceipt] = useState(emptyReceipt());

  const load = () => {
    api.get("/transactions").then((r) => setTxns(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/items").then((r) => setItems(r.data));
  };
  useEffect(() => { load(); }, []);

  const resetForm = () => { setEditId(null); setForm({ item_id: "", type: "keluar", qty: 1, date: today(), note: "", tipe: "Offline" }); };
  const openCreate = () => { resetForm(); setOpen(true); };
  const openEdit = (t) => {
    setEditId(t.id);
    setForm({ item_id: t.item_id, type: t.type, qty: t.qty, date: String(t.date).slice(0, 10), note: t.note || "", tipe: t.channel || t.tipe || "Offline" });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.item_id) return toast.error("Pilih barang dulu");
    const payload = { ...form, qty: Number(form.qty) };
    try {
      if (editId) {
        await api.put(`/transactions/${editId}`, payload);
        toast.success("Transaksi diperbarui, stok disesuaikan");
      } else {
        await api.post("/transactions", payload);
        toast.success("Transaksi dicatat, stok diperbarui");
      }
      setOpen(false); resetForm(); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  // ---- Terima Barang (multi-line goods receipt) ----
  const openReceipt = () => { setReceipt(emptyReceipt()); setReceiptOpen(true); };
  const addLine = () => setReceipt((r) => ({ ...r, lines: [...r.lines, { item_id: "", qty: 1, note: "" }] }));
  const removeLine = (idx) => setReceipt((r) => ({ ...r, lines: r.lines.filter((_, i) => i !== idx) }));
  const setLine = (idx, k, v) => setReceipt((r) => ({ ...r, lines: r.lines.map((l, i) => (i === idx ? { ...l, [k]: v } : l)) }));
  const saveReceipt = async (e) => {
    e.preventDefault();
    const lines = receipt.lines.filter((l) => l.item_id).map((l) => ({ item_id: l.item_id, qty: Number(l.qty), note: l.note || "" }));
    if (!lines.length) return toast.error("Tambahkan minimal 1 barang");
    if (lines.some((l) => !l.qty || l.qty <= 0)) return toast.error("Jumlah tiap barang harus lebih dari 0");
    try {
      await api.post("/transactions/receipt", { date: receipt.date, brand: receipt.brand, lines });
      toast.success(`Terima barang tercatat • ${lines.length} barang, stok diperbarui`);
      setReceiptOpen(false); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const removeTxn = async (t) => {
    const m = meta[t.type] || meta.masuk;
    if (!window.confirm(`Hapus transaksi ${m.label} ${t.qty} ${t.unit} — ${t.item_name}? Stok akan dikoreksi.`)) return;
    try {
      await api.delete(`/transactions/${t.id}`);
      toast.success("Transaksi dihapus, stok dikoreksi");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const toggleFound = async (t) => {
    try {
      await api.patch(`/transactions/${t.id}/found?found=${!t.found}`);
      toast.success(!t.found ? "Ditandai ditemukan, stok dikembalikan" : "Ditandai belum ditemukan, stok dikurangi");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const exportMasukPdf = async () => {
    const p = new URLSearchParams();
    if (from) p.set("start", from);
    if (to) p.set("end", to);
    if (qItem) p.set("q", qItem);
    if (qTipe) p.set("tipe", qTipe);
    try {
      const res = await fetch(`${API}/transactions/masuk/pdf?${p.toString()}`, {
        credentials: "include", headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!res.ok) throw new Error("Gagal mengunduh");
      const blob = await res.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "surat_terima_barang.pdf"; a.click(); URL.revokeObjectURL(url);
      toast.success("PDF Surat Terima Barang diunduh");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const doExportAll = async (fmt) => {
    try { await exportReport("transactions", fmt, { start: from, end: to, filename: "laporan_mutasi" }); toast.success("Laporan mutasi diunduh"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const shown = txns.filter((t) => {
    const d = String(t.date).slice(0, 10);
    const okType = filter === "all" || t.type === filter;
    const okDate = (!from || d >= from) && (!to || d <= to);
    const okItem = !qItem || `${t.item_name} ${t.warna || ""}`.toLowerCase().includes(qItem.trim().toLowerCase());
    const okTipe = !qTipe || (t.item_tipe || "").toLowerCase().includes(qTipe.trim().toLowerCase());
    return okType && okDate && okItem && okTipe;
  });

  // group barang masuk (that share a batch) into receipt cards; keep others as single rows
  const buildBlocks = (list) => {
    const batches = {};
    const blocks = [];
    list.forEach((t) => {
      if (t.type === "masuk" && t.batch_id) {
        if (!batches[t.batch_id]) {
          batches[t.batch_id] = { kind: "receipt", key: `r-${t.batch_id}`, date: String(t.date).slice(0, 10), brand: t.brand || "", user_name: t.user_name || "", items: [] };
          blocks.push(batches[t.batch_id]);
        }
        batches[t.batch_id].items.push(t);
      } else {
        blocks.push({ kind: "single", key: `s-${t.id}`, date: String(t.date).slice(0, 10), txn: t });
      }
    });
    return blocks.sort((a, b) => String(b.date).localeCompare(String(a.date)));
  };
  const blocks = buildBlocks(shown);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="space-y-5 animate-fadeup">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-extrabold">Mutasi Stok</h1>
          <p className="text-[#52525B] text-sm">Terima barang (surat terima), keluar, retur & hilang</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="add-receipt-button" onClick={openReceipt} className="nb-btn px-4 py-2.5 flex items-center gap-2 text-[#0A0A0A]" style={{ background: "var(--masuk)" }}>
            <Truck className="w-5 h-5" strokeWidth={2.5} /> Terima Barang
          </button>
          <button data-testid="add-txn-button" onClick={openCreate} className="nb-btn px-4 py-2.5 flex items-center gap-2 bg-white text-[#0A0A0A]">
            <Plus className="w-5 h-5" strokeWidth={2.5} /> Catat Lain
          </button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {["all", "masuk", "keluar", "retur", "hilang"].map((f) => (
          <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 border-2 border-[#0A0A0A] rounded-lg text-sm font-bold ${filter === f ? "nb-shadow" : "bg-white"}`}
            style={filter === f ? { background: f === "all" ? "var(--primary)" : meta[f].color } : {}}>
            {f === "all" ? "Semua" : meta[f].label}
          </button>
        ))}
      </div>

      {/* Search + date filters */}
      <div className="nb-card p-3 space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="label-tag block mb-1">Cari barang (lihat tgl masuknya)</label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#52525B]" />
              <input data-testid="txn-search-item" value={qItem} onChange={(e) => setQItem(e.target.value)} placeholder="mis. Mobil Remote / Merah" className="nb-input pl-8" />
            </div>
          </div>
          <div>
            <label className="label-tag block mb-1">Cari tipe barang</label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#52525B]" />
              <input data-testid="txn-search-tipe" value={qTipe} onChange={(e) => setQTipe(e.target.value)} placeholder="mis. Mobil-mobilan" className="nb-input pl-8" />
            </div>
          </div>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label className="label-tag block mb-1">Dari tanggal</label>
            <input data-testid="txn-date-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="nb-input w-auto" />
          </div>
          <div>
            <label className="label-tag block mb-1">Sampai tanggal</label>
            <input data-testid="txn-date-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="nb-input w-auto" />
          </div>
          {(from || to || qItem || qTipe) && (
            <button data-testid="txn-date-reset" onClick={() => { setFrom(""); setTo(""); setQItem(""); setQTipe(""); }} className="nb-btn px-3 py-2 bg-white text-sm">Reset</button>
          )}
          <button data-testid="export-masuk-pdf" onClick={exportMasukPdf} className="nb-btn px-3 py-2 text-sm flex items-center gap-1 text-[#0A0A0A]" style={{ background: "var(--primary)" }}>
            <FileDown className="w-4 h-4" /> PDF Terima Barang
          </button>
          <button data-testid="export-txn-excel" onClick={() => doExportAll("excel")} className="nb-btn px-3 py-2 bg-white text-sm flex items-center gap-1"><FileDown className="w-4 h-4" /> Excel Mutasi</button>
          <button data-testid="export-txn-pdf" onClick={() => doExportAll("pdf")} className="nb-btn px-3 py-2 bg-white text-sm flex items-center gap-1"><FileDown className="w-4 h-4" /> PDF Mutasi</button>
        </div>
      </div>

      {blocks.length === 0 ? (
        <div className="nb-card p-10 text-center text-[#52525B]">Belum ada transaksi.</div>
      ) : (
        <div className="space-y-3">
          {blocks.map((b) => b.kind === "receipt" ? (
            <div key={b.key} data-testid={`receipt-${b.key}`} className="nb-card p-4">
              <div className="flex items-center justify-between gap-2 mb-3 pb-2 border-b-2 border-[#0A0A0A]">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-9 h-9 rounded-lg border-2 border-[#0A0A0A] flex items-center justify-center shrink-0" style={{ background: "var(--masuk)" }}>
                    <Truck className="w-5 h-5" strokeWidth={2.5} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-extrabold truncate">Terima Barang • {b.date}</div>
                    <span className="label-tag">Supplier: <b>{b.brand || "—"}</b> • {b.items.length} barang</span>
                  </div>
                </div>
                <div className="font-display font-extrabold text-lg shrink-0">{b.items.reduce((s, i) => s + (Number(i.qty) || 0), 0)} <span className="text-xs font-semibold">pcs</span></div>
              </div>
              <div className="space-y-1.5">
                {b.items.map((t) => (
                  <div key={t.id} data-testid={`txn-row-${t.id}`} className="flex items-center gap-2 border-2 border-[#0A0A0A] rounded-lg px-3 py-1.5">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm truncate">{t.item_name}{t.warna ? ` — ${t.warna}` : ""}</div>
                      {(t.item_tipe || t.note) && <span className="label-tag">{t.item_tipe ? `Tipe: ${t.item_tipe}` : ""}{t.item_tipe && t.note ? " • " : ""}{t.note || ""}</span>}
                    </div>
                    <div className="font-display font-extrabold">{t.qty} <span className="text-xs font-semibold">{t.unit}</span></div>
                    {canEdit && (
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button data-testid={`txn-edit-${t.id}`} onClick={() => openEdit(t)} className="w-8 h-8 border-2 border-[#0A0A0A] rounded-lg flex items-center justify-center bg-white"><Pencil className="w-4 h-4" /></button>
                        <button data-testid={`txn-delete-${t.id}`} onClick={() => removeTxn(t)} className="w-8 h-8 border-2 border-[#0A0A0A] rounded-lg flex items-center justify-center text-white" style={{ background: "var(--retur)" }}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            (() => {
              const t = b.txn;
              const m = meta[t.type] || meta.masuk;
              return (
                <div key={b.key} data-testid={`txn-row-${t.id}`} className="nb-card p-3 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg border-2 border-[#0A0A0A] flex items-center justify-center shrink-0" style={{ background: m.color }}>
                    <m.icon className="w-5 h-5" strokeWidth={2.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{t.item_name}{t.warna ? ` — ${t.warna}` : ""}</div>
                    <div className="label-tag">{m.label} • {t.date}{t.item_tipe ? ` • ${t.item_tipe}` : ""}{t.type === "retur" && (t.channel || t.tipe) ? ` • dari ${t.channel || t.tipe}` : ""}{t.brand ? ` • ${t.brand}` : ""}{t.note ? ` • ${t.note}` : ""}</div>
                    {t.type === "hilang" && (
                      <label data-testid={`txn-found-${t.id}`} className="inline-flex items-center gap-1.5 mt-1 text-xs font-bold cursor-pointer select-none">
                        <input type="checkbox" checked={!!t.found} onChange={() => toggleFound(t)} className="w-4 h-4 accent-[var(--masuk)]" />
                        <span style={{ color: t.found ? "var(--masuk)" : "var(--retur)" }}>{t.found ? "Sudah ditemukan" : "Belum ditemukan"}</span>
                      </label>
                    )}
                    {t.edited_by && (
                      <span data-testid={`txn-edited-${t.id}`} className="inline-flex items-center gap-1 mt-1 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 border-2 border-[#0A0A0A] rounded" style={{ background: "var(--primary)" }}>
                        <Pencil className="w-3 h-3" /> diedit oleh {t.edited_by}
                      </span>
                    )}
                  </div>
                  <div className="font-display font-extrabold text-lg">{t.qty} <span className="text-xs font-semibold">{t.unit}</span></div>
                  {canEdit && (
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button data-testid={`txn-edit-${t.id}`} onClick={() => openEdit(t)} className="w-8 h-8 border-2 border-[#0A0A0A] rounded-lg flex items-center justify-center bg-white"><Pencil className="w-4 h-4" /></button>
                      <button data-testid={`txn-delete-${t.id}`} onClick={() => removeTxn(t)} className="w-8 h-8 border-2 border-[#0A0A0A] rounded-lg flex items-center justify-center text-white" style={{ background: "var(--retur)" }}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  )}
                </div>
              );
            })()
          ))}
        </div>
      )}

      {/* Terima Barang (Surat Terima Barang) modal */}
      <Modal open={receiptOpen} onClose={() => setReceiptOpen(false)} title="Terima Barang (Surat Terima)" testid="receipt-modal">
        <form onSubmit={saveReceipt}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tanggal"><input data-testid="receipt-date" type="date" required value={receipt.date} onChange={(e) => setReceipt({ ...receipt, date: e.target.value })} className="nb-input" /></Field>
            <Field label="Brand / Supplier"><input data-testid="receipt-brand" value={receipt.brand} onChange={(e) => setReceipt({ ...receipt, brand: e.target.value })} className="nb-input" placeholder="mis. PT Mainan Jaya" /></Field>
          </div>
          <label className="label-tag block mb-1 mt-1">Daftar Barang Masuk</label>
          <div className="space-y-2">
            {receipt.lines.map((ln, idx) => (
              <div key={idx} data-testid={`receipt-line-${idx}`} className="border-2 border-[#0A0A0A] rounded-lg p-2 space-y-2">
                <ItemCombo testid={`receipt-item-${idx}`} value={ln.item_id} items={items} onSelect={(id) => setLine(idx, "item_id", id)} />
                <div className="flex items-center gap-2">
                  <input data-testid={`receipt-qty-${idx}`} type="number" min="1" value={ln.qty} onChange={(e) => setLine(idx, "qty", e.target.value)} className="nb-input w-24" placeholder="Qty" />
                  <input data-testid={`receipt-note-${idx}`} value={ln.note} onChange={(e) => setLine(idx, "note", e.target.value)} className="nb-input flex-1" placeholder="Catatan (opsional)" />
                  {receipt.lines.length > 1 && (
                    <button type="button" data-testid={`receipt-remove-${idx}`} onClick={() => removeLine(idx)} className="w-9 h-9 border-2 border-[#0A0A0A] rounded-lg flex items-center justify-center text-white shrink-0" style={{ background: "var(--retur)" }}><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <button type="button" data-testid="receipt-add-line" onClick={addLine} className="nb-btn w-full py-2 mt-2 bg-white text-sm flex items-center justify-center gap-1"><PackagePlus className="w-4 h-4" /> Tambah Barang</button>
          <button data-testid="save-receipt-button" type="submit" className="nb-btn w-full py-3 mt-2 text-[#0A0A0A]" style={{ background: "var(--masuk)" }}>Simpan Terima Barang</button>
        </form>
      </Modal>

      {/* Single transaction modal (keluar / retur / hilang / masuk edit) */}
      <Modal open={open} onClose={() => { setOpen(false); resetForm(); }} title={editId ? "Edit Transaksi" : "Catat Transaksi"} testid="txn-modal">
        <form onSubmit={save}>
          <Field label="Jenis">
            <div className="grid grid-cols-2 gap-2">
              {["masuk", "keluar", "retur", "hilang"].map((tp) => (
                <button type="button" key={tp} data-testid={`type-${tp}`} onClick={() => setForm({ ...form, type: tp })}
                  className={`py-2 border-2 border-[#0A0A0A] rounded-lg text-sm font-bold capitalize ${form.type === tp ? "nb-shadow" : "bg-white"}`}
                  style={form.type === tp ? { background: meta[tp].color } : {}}>{tp}</button>
              ))}
            </div>
          </Field>
          {form.type === "retur" && (
            <Field label="Retur dari (Marketplace / Offline)">
              <select data-testid="txn-channel" value={form.tipe} onChange={set("tipe")} className="nb-input">
                {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          )}
          <Field label="Barang">
            <ItemCombo testid="txn-item-select" value={form.item_id} items={items} onSelect={(id) => setForm({ ...form, item_id: id })} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Jumlah"><input data-testid="txn-qty" type="number" min="1" required value={form.qty} onChange={set("qty")} className="nb-input" /></Field>
            <Field label="Tanggal"><input data-testid="txn-date" type="date" required value={form.date} onChange={set("date")} className="nb-input" /></Field>
          </div>
          <Field label={form.type === "hilang" ? "Keterangan (mis. sebab hilang)" : "Catatan (opsional)"}><input data-testid="txn-note" value={form.note} onChange={set("note")} className="nb-input" /></Field>
          <button data-testid="save-txn-button" type="submit" className="nb-btn w-full py-3 mt-1 text-[#0A0A0A]" style={{ background: "var(--primary)" }}>{editId ? "Simpan Perubahan" : "Simpan Transaksi"}</button>
        </form>
      </Modal>
    </div>
  );
}
